import { Button, PlusIcon, Search } from '@postidigital/posti-components'
import { toJS } from 'mobx'
import { observer } from 'mobx-react-lite'
import React, { ChangeEventHandler, useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { StoreContext } from '../../store'
import { breakpoint } from '../../utils/breakpoint'
import { arrayIsEmpty } from '../../utils/helpers'
import { useFeatureNavigation } from '../../utils/useFeatureNavigation'
import { FilterButton } from '../buttons/buttons'
import { FilterModal } from '../FilterModal/FilterModal'

const SearchAndFilterComponent: React.FC = () => {
  const { t } = useTranslation()
  const { layoutStore, searchStore } = useContext(StoreContext)
  const { navigate } = useFeatureNavigation()
  const { isMobile } = layoutStore
  const [filterOpen, setFilterOpen] = useState<boolean>(false)
  const { searchTerm, setSearchTerm } = searchStore

  const { searchMainUsers, selectedOrganisations } = searchStore

  const selectedOrganisationsToJS = toJS(selectedOrganisations)
  const orgsSelected = !arrayIsEmpty(selectedOrganisationsToJS)
  const filtersSelected = Boolean(orgsSelected || searchMainUsers)

  const handleToggleFilters = useCallback(() => {
    setFilterOpen((prevState) => !prevState)
  }, [])

  const onButtonClick = useCallback(() => {
    navigate('/invite/1')
  }, [navigate])

  const handleSearch: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      e.preventDefault()

      if (!e?.target?.value) {
        return setSearchTerm('')
      }

      setSearchTerm(e.target.value)

      return searchStore.debouncedFetchResults()
    },
    [searchStore, setSearchTerm]
  )

  useEffect(() => {
    if (!isMobile) {
      setFilterOpen(false)
    }
  }, [isMobile])

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
      {isMobile && (
        <>
          <FilterButton selected={filtersSelected} onClick={handleToggleFilters} />
          <FilterModal filterOpen={filterOpen} handleToggleFilters={handleToggleFilters} />
        </>
      )}

      <StyledSearch>
        <Search
          onChange={handleSearch}
          debounceTime={1000}
          value={searchTerm}
          label={t(`landing.search`)}
          id="search-1"
          clearButtonProps={{
            id: 'search_1_clear_button',
            onClick: () => setSearchTerm(''),
          }}
        />
      </StyledSearch>

      <Button
        css
        rightIcon={PlusIcon}
        id="invite-user-button"
        onClick={onButtonClick}
        hasBackground={!isMobile}
        size={isMobile ? 'sm' : 'md'}
        contentMode={isMobile ? 'tertiary' : 'primary'}
      >
        {t(`landing.inviteUser`)}
      </Button>
    </div>
  )
}

export const SearchAndFilter = observer(SearchAndFilterComponent)

const StyledSearch = styled('div')`
  &:first-child {
    display: flex;
    flex: 1;
    min-width: min-content;
    max-width: 100%;

    & > div {
      width: 100%;
    }
  }

  @media ${breakpoint.mobile} {
    min-width: 100%;
    order: 3;
  }
`
